/**
 * A trailer's faults and warnings, explained (GET /api/v1/admin/units/:unitId/device-events/faults).
 * Staff only on the server: anyone else gets 403, which reads here as "nothing to show".
 * Shape: openapi/pocket-api.yaml `UnitFaultSummary`.
 */
import { authenticatedFetch } from '@/lib/api';

export type CatalogueSeverity = 'critical' | 'fault' | 'warning' | 'info' | 'ignore';

export interface FaultCatalogueEntry {
  severity: CatalogueSeverity | string;
  message: string;
  recommended_action: string | null;
  manual_reference: string | null;
  vendor_name: string | null;
  /** Wording not yet approved by Hollywood Trucks. */
  draft: boolean;
}

export interface UnitFaultRow {
  code: string;
  vendor_code: string;
  vendor_name: string | null;
  source: string;
  system: string;
  /** The event's kind as the device reported it: fault or warning. */
  severity: 'fault' | 'warning' | string;
  count: number;
  first_seen: string;
  last_seen: string;
  catalogue: FaultCatalogueEntry | null;
}

export interface UnitClearedFaultRow extends UnitFaultRow {
  cleared_at: string;
  cleared_by: 'acknowledged' | 'aged_out';
}

export interface UnitFaultSummary {
  window_days: number;
  counts: { fault: number; warning: number };
  active: UnitFaultRow[];
  recently_cleared: UnitClearedFaultRow[];
}

/** null when the caller may not see device errors (403): the page then shows no section. */
export async function getUnitFaults(unitId: string): Promise<UnitFaultSummary | null> {
  const res = await authenticatedFetch(`/api/v1/admin/units/${encodeURIComponent(unitId)}/device-events/faults`);
  if (res.status === 403) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as UnitFaultSummary;
}
